/**
 * This component scrolls to the last mounted component
 * <ScrollToLastMounted.Point/>.
 *
 * @usage
 * <ScrollToLastMounted offset={-50}>
 *     <ScrollToLastMounted.Point/>
 * </ScrollToLastMounted>
 */
import {
    ReactNode,
    RefObject,
    createContext,
    useCallback,
    useContext,
    useLayoutEffect,
    useRef,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';

type ScrollToLastMountedContextType = {
    onMounted: (ref: RefObject<HTMLElement>) => void;
};

export const ScrollToLastMountedContext =
    createContext<ScrollToLastMountedContextType>({
        onMounted: () => {
            /* noop */
        },
    });

type ScrollToLastMountedProps = { children: ReactNode; offset?: number };

const ScrollToLastMounted = ({
    children,
    offset = 0,
}: ScrollToLastMountedProps) => {
    const scrollToElement = useDebouncedCallback((el: HTMLElement) => {
        const y = el.getBoundingClientRect().top + window.scrollY + offset;

        window.scrollTo({ top: y, behavior: 'smooth' });
    });

    const handleMounted = useCallback(
        (ref: RefObject<HTMLElement>) => {
            if (!ref.current) {
                return;
            }

            scrollToElement(ref.current);
        },
        [scrollToElement]
    );

    return (
        <ScrollToLastMountedContext.Provider
            value={{
                onMounted: handleMounted,
            }}
        >
            {children}
        </ScrollToLastMountedContext.Provider>
    );
};

type ScrollToLastMountedPointProps = {
    deps?: Array<any>;
};

const ScrollToLastMountedPoint = ({
    deps = [],
}: ScrollToLastMountedPointProps) => {
    const ref = useRef<HTMLDivElement>(null);

    const { onMounted } = useContext(ScrollToLastMountedContext);

    useLayoutEffect(() => {
        onMounted(ref);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onMounted, ...deps]);

    return <div ref={ref} />;
};

ScrollToLastMounted.Point = ScrollToLastMountedPoint;

export default ScrollToLastMounted;
